import React, { useCallback, useEffect, useState } from 'react';
import { ShoppingCartIcon, TrendingUpIcon, CalendarIcon, PackageIcon, BarChart3Icon, FileTextIcon, MapPinIcon, RefreshCwIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Pagination } from '../components/ui/Pagination';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { supabaseAdmin } from '../lib/supabase';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
interface SaleItem {
  id: string;
  clienteNombre: string;
  vendedorId: string;
  vendedorNombre: string;
  fecha: Date;
  zona: string;
  montoTotal: number;
  subTotal: number;
  itbis: number;
  referencia: string;
  comentarios: string;
  source: 'visita' | 'factura';
}
export function SalesPage() {
  const {
    user
  } = useAuth();
  const {
    visits,
    users,
    totalVendido: totalVendidoGlobal,
    isLoading: appLoading
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const canSeeAll = isAdmin || user?.permisos?.cuentas_cobrar === true;
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Only actual vendedores (users with new-visit permission)
  const vendedorIds = new Set(users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.id));
  const vendedorNames = new Set(users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.nombre));
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  // Load invoices from Supabase
  const [invoices, setInvoices] = useState<any[]>([]);
  const loadInvoices = useCallback(async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('invoices').select('id,numero_factura,farmacia,vendedor,vendedor_id,fecha_emision,sub_total,itbis,monto,estado').not('estado', 'eq', 'anulada').order('fecha_emision', {
        ascending: false
      }).limit(2000);
      if (data) setInvoices(data);
      if (error) console.error('Error loading invoices for sales:', error);
    } catch {}
  }, []);
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);
  useRealtimeRefresh(['invoices'], loadInvoices);
  // Use ONLY invoices as the authoritative source for sales.
  // Visit-sales are unreliable because pedidos can be deleted after the visit
  // was recorded, leaving stale huboVenta=true entries.
  const allSales = invoices.map(inv => ({
    id: `f-${inv.id}`,
    clienteNombre: inv.farmacia,
    vendedorId: inv.vendedor_id || '',
    vendedorNombre: inv.vendedor || '',
    fecha: new Date(inv.fecha_emision),
    zona: '',
    montoTotal: inv.monto || 0,
    subTotal: inv.sub_total || 0,
    itbis: inv.itbis || 0,
    referencia: inv.numero_factura || '',
    comentarios: '',
    source: 'factura' as const
  }) as SaleItem).filter(s => canSeeAll || s.vendedorId === user?.id).filter(s => {
    const saleDate = s.fecha.toLocaleDateString('en-CA', {
      timeZone: 'America/Santo_Domingo'
    });
    if (dateFrom && saleDate < dateFrom) return false;
    if (dateTo && saleDate > dateTo) return false;
    return true;
  }).sort((a, b) => b.fecha.getTime() - a.fecha.getTime());
  // ── Totals from filtered data — respond to date range and source filters ──
  const totalVendido = allSales.reduce((sum, s) => sum + s.montoTotal, 0);
  const totalPedidos = allSales.length;
  const promedioVenta = totalPedidos > 0 ? totalVendido / totalPedidos : 0;
  const today = new Date();
  const todaySales = allSales.filter(s => s.fecha.toDateString() === today.toDateString());
  const todayTotal = todaySales.reduce((sum, s) => sum + s.montoTotal, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => date.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short'
  });
  const totalPages = Math.ceil(allSales.length / ITEMS_PER_PAGE);
  const paginatedSales = allSales.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Map vendedorId -> canonical name from profiles
  const vendedorIdToName: Record<string, string> = {};
  users.filter(u => u.activo && u.permisos?.['new-visit'] === true).forEach(u => {
    vendedorIdToName[u.id] = u.nombre;
  });
  // Also map vendedorName variations -> canonical name (case-insensitive, trimmed)
  const vendedorNameNormalized: Record<string, string> = {};
  users.filter(u => u.activo && u.permisos?.['new-visit'] === true).forEach(u => {
    vendedorNameNormalized[u.nombre.trim().toLowerCase()] = u.nombre;
  });
  const salesByVendor = isAdmin ? allSales.filter(s => vendedorIds.has(s.vendedorId) || vendedorNames.has(s.vendedorNombre) || vendedorNameNormalized[s.vendedorNombre.trim().toLowerCase()]).reduce((acc, s) => {
    // Resolve canonical name: prefer ID match, then normalized name match
    const name = vendedorIdToName[s.vendedorId] || vendedorNameNormalized[s.vendedorNombre.trim().toLowerCase()] || s.vendedorNombre || 'Sin vendedor';
    if (!acc[name]) acc[name] = {
      total: 0,
      count: 0
    };
    acc[name].total += s.montoTotal;
    acc[name].count += 1;
    return acc;
  }, {} as Record<string, {
    total: number;
    count: number;
  }>) : {};
  return <div className="space-y-6" data-id="element-4118">
      <PageHeader title="Registro de Ventas" subtitle="Historial de ventas y facturación" icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-4120" />} data-id="element-4119">
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap" data-id="element-4121">
          <div className="flex items-center gap-1.5" data-id="element-4122">
            <span className="text-xs text-slate-500 font-medium" data-id="element-4123">Desde</span>
            <input type="date" value={dateFrom} onChange={e => {
            setDateFrom(e.target.value);
            setCurrentPage(1);
          }} className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4124" />
          </div>
          <div className="flex items-center gap-1.5" data-id="element-4125">
            <span className="text-xs text-slate-500 font-medium" data-id="element-4126">Hasta</span>
            <input type="date" value={dateTo} onChange={e => {
            setDateTo(e.target.value);
            setCurrentPage(1);
          }} className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4127" />
          </div>

          <Button variant="secondary" onClick={() => loadInvoices()} title="Recargar facturas" data-id="element-4128">
            <RefreshCwIcon className="w-4 h-4" data-id="element-4129" />
          </Button>
          {(dateFrom || dateTo) && <Button variant="secondary" onClick={() => {
          setDateFrom('');
          setDateTo('');
        }} data-id="element-4130">
              Limpiar
            </Button>}
        </div>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-4131">
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-4133" />} variant="primary" data-id="element-4132" />
        <StatsCard title="Ventas Hoy" value={formatCurrency(todayTotal)} subtitle={`${todaySales.length} registros`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-4135" />} variant="success" data-id="element-4134" />
        <StatsCard title="Total Registros" value={totalPedidos} icon={<PackageIcon className="w-5 h-5" data-id="element-4137" />} variant="warning" data-id="element-4136" />
        <StatsCard title="Promedio por Venta" value={formatCurrency(promedioVenta)} icon={<BarChart3Icon className="w-5 h-5" data-id="element-4139" />} variant="default" data-id="element-4138" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4140">
        {/* Sales list */}
        <Card padding="none" className="lg:col-span-2 animate-stagger-2" data-id="element-4141">
          <div className="px-6 py-5 border-b border-slate-100" data-id="element-4142">
            <CardHeader title="Historial de Ventas" subtitle={`${allSales.length} ventas registradas`} data-id="element-4143" />
          </div>

          {appLoading && allSales.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-4144">
              <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-4145" />
              <p className="text-slate-500 text-sm" data-id="element-4146">Cargando ventas...</p>
            </div> : allSales.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-4147">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-4148">
                <ShoppingCartIcon className="w-8 h-8 text-slate-400" data-id="element-4149" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-4150">
                No hay ventas
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-4151">
                No hay ventas registradas en el período seleccionado
              </p>
            </div> : <>
              <div className="divide-y divide-slate-100" data-id="element-4152">
                {paginatedSales.map(sale => <div key={sale.id} className="px-6 py-4 hover:bg-slate-50/50 transition-colors" data-id="element-4153">
                    <div className="flex items-start justify-between" data-id="element-4154">
                      <div className="flex-1" data-id="element-4155">
                        <div className="flex items-center gap-3" data-id="element-4156">
                          <h4 className="font-semibold text-slate-900" data-id="element-4157">
                            {sale.clienteNombre}
                          </h4>
                          {sale.referencia && <Badge variant={sale.source === 'factura' ? 'warning' : 'info'} data-id="element-4158">
                              {sale.referencia}
                            </Badge>}
                          <Badge variant={sale.source === 'factura' ? 'default' : 'success'} data-id="element-4159">
                            {sale.source === 'factura' ? <span className="flex items-center gap-1" data-id="element-4160">
                                <FileTextIcon className="w-3 h-3" data-id="element-4161" />
                                Factura
                              </span> : <span className="flex items-center gap-1" data-id="element-4162">
                                <MapPinIcon className="w-3 h-3" data-id="element-4163" />
                                Visita
                              </span>}
                          </Badge>
                        </div>
                        <div className="mt-1 flex items-center gap-4 text-sm text-slate-500" data-id="element-4164">
                          {(isAdmin || canSeeAll) && sale.vendedorNombre && <span className="font-medium" data-id="element-4165">
                              {sale.vendedorNombre}
                            </span>}
                          <span data-id="element-4166">{formatDate(sale.fecha)}</span>
                          {sale.zona && <span data-id="element-4167">{sale.zona}</span>}
                        </div>
                        {sale.comentarios && <p className="mt-2 text-sm text-slate-600" data-id="element-4168">
                            {sale.comentarios}
                          </p>}
                      </div>
                      <div className="text-right flex-shrink-0 ml-4" data-id="element-4169">
                        <p className="text-xl font-bold text-[#2952C4]" data-id="element-4170">
                          {formatCurrency(sale.montoTotal)}
                        </p>
                        <div className="mt-1 text-xs text-slate-500 space-y-0.5" data-id="element-4171">
                          <p data-id="element-4172">
                            Sub Total:{' '}
                            <span className="font-semibold text-slate-700" data-id="element-4173">
                              {formatCurrency(sale.subTotal)}
                            </span>
                          </p>
                          <p data-id="element-4174">
                            ITBIS:{' '}
                            <span className="font-semibold text-slate-600" data-id="element-4175">
                              {formatCurrency(sale.itbis)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
              <div className="animate-stagger-3" data-id="element-4176">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={allSales.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-4177" />
              </div>
            </>}
        </Card>

        {/* Sales by vendor (admin only) */}
        {isAdmin && <Card className="animate-stagger-3" data-id="element-4178">
            <CardHeader title="Ventas por Vendedor" subtitle={`Basado en ${invoices.length} facturas`} data-id="element-4179" />
            <div className="mt-4 space-y-4" data-id="element-4180">
              {Object.entries(salesByVendor).sort((a, b) => b[1].total - a[1].total).map(([vendedor, data]) => <div key={vendedor} className="p-4 bg-slate-50 rounded-xl" data-id="element-4181">
                    <div className="flex items-center justify-between mb-2" data-id="element-4182">
                      <div data-id="element-4183">
                        <p className="font-semibold text-slate-900" data-id="element-4184">
                          {vendedor}
                        </p>
                        <p className="text-sm text-slate-500" data-id="element-4185">
                          {data.count} ventas
                        </p>
                      </div>
                      <p className="text-lg font-bold text-[#2952C4]" data-id="element-4186">
                        {formatCurrency(data.total)}
                      </p>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-4187">
                      <div className="bg-[#2952C4] h-2 rounded-full transition-all" style={{
                width: `${data.total / Math.max(...Object.values(salesByVendor).map(d => d.total), 1) * 100}%`
              }} data-id="element-4188" />
                    </div>
                  </div>)}
            </div>
          </Card>}
      </div>
    </div>;
}